/**
 * 배포 팝업 — 점검 → 대상(메뉴 · 쿼리 이름 · 중복 확인) → 배포. 2026-09-30 (01_결정.md 20 · 21).
 *
 * 메뉴는 새 화면을 만들지 않고 기존 노츠메뉴 도움창(PopupSettings.POPUP_MENU, SinglePopup)을 그대로 연다.
 * 고르는 순간 BE 점검(deployCheck)을 불러 동기화 · 말단 · 패키지/사용자 정의 · 이미 배포됨 · 쿼리 이름 충돌을 보여 준다.
 * 재배포(이미 대상 메뉴가 있는 이슈)는 메뉴 · 쿼리 이름을 채운 채 열린다.
 * showPopupModal 의 규약대로 getSelectedRow 로 { cd_menu, cd_query } 를 돌려준다.
 */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import SnButton from "@shared/components/SnButton";
import { showDialog } from "@widgets/CommonDialog";
import { showPopupModal } from "@widgets/CommonModal";
import SinglePopup from "@widgets/commonpopup/SinglePopup";
import { PopupSettings } from "@widgets/commonpopup/PopupSettings";
import { DeployCheck, deployCheck, DesignDraft, MAIN_QUERY } from "@shared/api/designApi";
import { hasScreenModule } from "@shared/libs/screenModules";

interface Props {
  draft: DesignDraft;
  /** 이 초안의 조회 쿼리를 마지막으로 실행해 성공했는가 (쿼리 탭 [실행] 또는 미리보기 조회) */
  lastRunOk: boolean;
  modalRef?: any;
}

interface CheckItem {
  ok: boolean;
  text: string;
  hint?: string;
}

const DeployPopup = forwardRef(({ draft, lastRunOk, modalRef }: Props, ref) => {
  const [cdMenu, setCdMenu] = useState<string>(draft.cdMenu || "");
  const [nmMenu, setNmMenu] = useState<string>("");
  const [cdQuery, setCdQuery] = useState<string>("");
  const [check, setCheck] = useState<DeployCheck | null>(null);
  const [checking, setChecking] = useState(false);
  const selectedRef = useRef<{ cd_menu: string; cd_query: string } | null>(null);

  /* ---------------------------------------------------------------- 1. 점검 — 초안 자체 */
  const items: CheckItem[] = useMemo(() => {
    const grid = draft.gridColumns || [];
    const ctrl = draft.controls || [];
    const badGrid = grid.filter((r) => !String(r.cd_column || "").trim() || !String(r.nm_column || "").trim()).length;
    /* 기간콤보 · 체크기간 칸은 앞의 콤보 · 체크가 라벨 역할이라 라벨이 비어 있는 게 정상이다 — 라벨 검사를 건너뛴다 (2026-10-02) */
    const LABEL_FREE = new Set(["SnComboPeriodBox", "SnCheckPeriodBox"]);
    const badCtrl = ctrl.filter((r) =>
      (!LABEL_FREE.has(String(r.tp_ctrl || "")) && !String(r.nm_label || "").trim()) || !String(r.cd_param || "").trim()).length;
    return [
      { ok: !!draft.queries?.[MAIN_QUERY], text: "조회 쿼리가 있습니다", hint: "AI Bot 에 말하거나 쿼리 탭에서 넣어 주세요." },
      { ok: lastRunOk, text: "조회 쿼리 실행이 성공했습니다", hint: "쿼리 탭 [실행] 또는 미리보기 조회로 한 번 돌려 주세요." },
      { ok: grid.length > 0, text: `그리드 칼럼이 있습니다 (${grid.length}개)`, hint: "그리드 칼럼이 하나도 없습니다." },
      { ok: badGrid === 0, text: "그리드 칼럼의 칼럼코드 · 칼럼명이 채워져 있습니다", hint: `${badGrid}개 행이 비어 있습니다.` },
      { ok: badCtrl === 0, text: "조회조건의 라벨 · 파라미터명이 채워져 있습니다", hint: `${badCtrl}개 행이 비어 있습니다.` },
    ];
  }, [draft, lastRunOk]);
  const draftOk = items.every((i) => i.ok);

  /* ---------------------------------------------------------------- 2. 대상 — 메뉴 · 쿼리 이름 · BE 점검 */
  const runCheck = useCallback(async (menu: string, query: string) => {
    if (!menu) {
      setCheck(null);
      return;
    }
    setChecking(true);
    try {
      const c = await deployCheck(menu, query || undefined);
      setCheck(c);
      if (c.nmMenu) setNmMenu(c.nmMenu);
      if (!query && c.defaultQueryId) setCdQuery(c.defaultQueryId);
    } catch (e: any) {
      setCheck(null);
      await showDialog("error", e?.message || "메뉴를 확인하지 못했습니다.");
    } finally {
      setChecking(false);
    }
  }, []);

  /* 재배포 — 대상 메뉴가 이미 있으면 채워서 연다 */
  useEffect(() => {
    if (draft.cdMenu) void runCheck(draft.cdMenu, "");
  }, [draft.cdMenu, runCheck]);

  const onPickMenu = async () => {
    const setting = PopupSettings.POPUP_MENU;
    const result: any = await showPopupModal({
      title: setting.title,
      width: setting.width,
      height: "75%",
      children: <SinglePopup code="POPUP_MENU" selectedData={{ str_search: "" }} />,
    });
    const picked = Array.isArray(result) ? result[0] : null;
    if (!picked?.cd_menu) return;
    const menu = String(picked.cd_menu).trim().toUpperCase();
    setCdMenu(menu);
    setNmMenu(String(picked.nm_menu || ""));
    setCdQuery("");
    await runCheck(menu, "");
  };

  /* 패키지 메뉴 판별은 FE 가 한다 — 그 이름의 소스 화면이 번들에 실제로 있는가. BE 는 화면 파일 칸만 알고 파일 유무는 모른다 */
  const isPackage = !!check && check.synced && hasScreenModule(check.nmFile);
  const menuProblems: string[] = [];
  if (check) {
    if (!check.synced) menuProblems.push("회사메뉴에 아직 없는 메뉴입니다. 메뉴 동기화 뒤 배포해 주세요.");
    else {
      if (!check.leaf) menuProblems.push("하위 메뉴가 있는 메뉴에는 배포할 수 없습니다. 말단 메뉴를 골라 주세요.");
      if (isPackage) menuProblems.push("패키지 메뉴에는 배포할 수 없습니다. 사용자 정의 메뉴로 등록한 메뉴를 골라 주세요.");
      if (check.queryUsedByOther) menuProblems.push("다른 메뉴가 쓰는 쿼리 이름입니다. 다른 이름으로 해 주세요.");
    }
  }
  const menuChanged = !!draft.cdMenu && !!cdMenu && draft.cdMenu.toUpperCase() !== cdMenu.toUpperCase();
  const canDeploy = draftOk && !!cdMenu && !!check && menuProblems.length === 0 && !checking && !!cdQuery.trim();

  /* ---------------------------------------------------------------- 3. 배포 — 팝업의 [확인] 이 배포다.
     팝업이 확인을 누를 때 checkValidity 를 먼저 부른다(validation: true). 여기서 막을 것을 막고 덮어쓰기 · 메뉴 바꾸기를 확인받는다 */
  useImperativeHandle(ref, () => ({
    checkValidity: async () => {
      if (!draftOk) return { flag: false, message: "점검의 빨간불을 고친 뒤 다시 열어 주세요." };
      if (!cdMenu) return { flag: false, message: "배포할 메뉴를 골라 주세요." };
      if (checking || !check) return { flag: false, message: "메뉴 확인이 끝날 때까지 잠시 기다려 주세요." };
      if (menuProblems.length > 0) return { flag: false, message: menuProblems[0] };
      if (!cdQuery.trim()) return { flag: false, message: "쿼리 이름을 넣어 주세요." };
      if (menuChanged) {
        const ok = await showDialog("confirm", `대상 메뉴를 바꿉니다. 옛 메뉴(${draft.cdMenu})의 화면은 지워져 빈 화면이 됩니다. 진행하시겠습니까?`);
        if (!ok) return { flag: false };
      }
      if (check.deployed) {
        const ok = await showDialog("confirm", "이미 배포된 화면이 있습니다. 지금 초안으로 덮어쓰고, 사용자별 화면 설정도 초기화합니다. 진행하시겠습니까?");
        if (!ok) return { flag: false };
      }
      selectedRef.current = { cd_menu: cdMenu, cd_query: cdQuery.trim().toLowerCase() };
      return { flag: true };
    },
    getSelectedRow: () => (selectedRef.current ? [selectedRef.current] : []),
  }));

  const Light = ({ ok }: { ok: boolean }) => (
    <span className={`inline-block w-2.5 h-2.5 rounded-full mr-2 ${ok ? "bg-emerald-500" : "bg-red-500"}`} />
  );

  return (
    <div className="w-full h-full min-h-0 flex flex-col gap-3 text-sm">
      {/* 1. 점검 */}
      <section className="border rounded p-3">
        <div className="font-semibold mb-2">1. 점검</div>
        <ul className="space-y-1">
          {items.map((i, idx) => (
            <li key={idx} className="flex items-start">
              <Light ok={i.ok} />
              <span>
                {i.text}
                {!i.ok && i.hint && <span className="ml-2 text-red-600">{i.hint}</span>}
              </span>
            </li>
          ))}
        </ul>
        {!draftOk && <div className="mt-2 text-red-600">빨간불을 고친 뒤 다시 열어 주세요.</div>}
      </section>

      {/* 2. 대상 */}
      <section className={`border rounded p-3 ${draftOk ? "" : "opacity-50 pointer-events-none"}`}>
        <div className="font-semibold mb-2">2. 대상</div>
        <div className="grid grid-cols-[90px_1fr_auto] gap-x-2 gap-y-2 items-center">
          <span className="text-gray-600">메뉴</span>
          <div>
            {cdMenu ? (
              <span>
                <span className="font-mono">{cdMenu}</span>
                {nmMenu && <span className="ml-2">{nmMenu}</span>}
              </span>
            ) : (
              <span className="text-gray-400">아직 고르지 않았습니다</span>
            )}
          </div>
          <SnButton text={cdMenu ? "바꾸기" : "메뉴 고르기"} onClick={onPickMenu} />

          <span className="text-gray-600">쿼리 이름</span>
          <input
            className="border rounded px-2 h-8 font-mono text-xs w-full"
            value={cdQuery}
            placeholder={check?.defaultQueryId || "메뉴를 먼저 고르세요"}
            disabled={!cdMenu}
            onChange={(e) => setCdQuery(e.target.value)}
            onBlur={() => void runCheck(cdMenu, cdQuery)}
          />
          <span className="text-xs text-gray-500">기본은 메뉴코드_01</span>
        </div>

        {checking && <div className="mt-2 text-gray-500">확인 중…</div>}
        {check && !checking && (
          <ul className="mt-2 space-y-1">
            <li><Light ok={check.synced} />회사메뉴에 동기화된 메뉴</li>
            <li><Light ok={check.synced && check.leaf} />말단 메뉴</li>
            <li>
              <Light ok={!isPackage} />
              {isPackage ? "패키지 메뉴" : "사용자 정의 메뉴"}
              {check.nmFile && (
                <span className="ml-2 text-gray-500">
                  (화면 파일 {check.nmFile}{isPackage ? " — 소스 화면 있음" : " — 소스 화면 없음"})
                </span>
              )}
            </li>
            <li><Light ok={!check.queryUsedByOther} />쿼리 이름 사용 가능</li>
            {check.deployed && <li className="text-amber-700">이미 배포된 화면이 있습니다 — 배포하면 덮어쓰고 사용자별 화면 설정도 초기화합니다.</li>}
            {menuChanged && <li className="text-amber-700">대상 메뉴를 바꾸면 옛 메뉴({draft.cdMenu})의 화면은 지워집니다.</li>}
          </ul>
        )}
        {menuProblems.map((p, i) => (
          <div key={i} className="mt-1 text-red-600">{p}</div>
        ))}
      </section>

      {/* 3. 배포 — 팝업 바닥의 [확인] 이 배포다 */}
      <div className={`text-right text-xs ${canDeploy ? "text-emerald-700" : "text-gray-500"}`}>
        {canDeploy ? "준비됐습니다. [확인] 을 누르면 배포합니다." : "위 항목이 모두 초록불이면 [확인] 으로 배포합니다."}
      </div>
    </div>
  );
});

export default DeployPopup;
